import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Upload, Loader2, Package, Image as ImageIcon, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ProtectedImg from "@/components/b2b/ProtectedImg";

const PLACES = ["Przód", "Tył", "Lewy rękaw", "Prawy rękaw", "Inne"];
const TECHNIQUES = ["DTF", "Haft", "Silikon", "Flock", "Inna"];
const pln = (g) => `${((g || 0) / 100).toFixed(2).replace(".", ",")} zł`;
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const vKey = (c, s) => `${c}|${s}`;

const EMPTY = {
  name: "", subtitle: "", description: "", price_grosz: 0, photos: [], colors: [], sizes: [],
  disabled_variants: [], size_table: "", sort_order: 0, no_branding: false,
  base_sku: "", base_color: "", base_notes: "", variant_index: {}, markings: [], production_notes: "",
};

export const B2bProducts = ({ storeId, storeName }) => {
  const confirm = useConfirm();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product obj or {} for new
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/b2b/admin/stores/${storeId}/products`).then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [storeId]);

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setOpen(true); };

  const del = async (p) => {
    if (!(await confirm({ title: `Usunąć produkt „${p.name}"?`, description: "Operacja nieodwracalna.", confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/b2b/admin/products/${p.id}`); toast.success("Produkt usunięty"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-lg border border-border bg-background/50 p-4 space-y-3" data-testid={`b2b-products-${storeId}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"><Package className="h-4 w-4 text-amber-400" />Produkty sklepu ({products.length})</p>
        <Button data-testid={`b2b-add-product-${storeId}`} size="sm" variant="outline" className="h-8 gap-1.5" onClick={openNew}><Plus className="h-3.5 w-3.5" />Dodaj produkt</Button>
      </div>
      {products.length === 0 && <p className="text-[11px] text-muted-foreground px-1">Brak produktów. Dodaj pierwszy produkt prywatny.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {products.map((p) => (
          <div key={p.id} data-testid={`b2b-product-${p.id}`} className="rounded-lg border border-border bg-card overflow-hidden">
            <button type="button" onClick={() => openEdit(p)} className="block w-full text-left">
              <div className="aspect-[4/3] bg-[#0D1117] grid place-items-center overflow-hidden">
                {p.photos?.[0] ? <img src={`${API}/files/${p.photos[0]}`} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
              </div>
              <div className="p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold truncate">{p.name || "(bez nazwy)"}</p>
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${p.status === "published" ? "bg-emerald-400/15 text-emerald-300" : "bg-secondary text-muted-foreground"}`}>{p.status === "published" ? "Opublikowany" : "Roboczy"}</span>
                </div>
                <p className="text-xs font-mono text-amber-300">{pln(p.price_grosz)}</p>
                <p className="text-[10px] text-muted-foreground">{(p.colors || []).length} kol. · {(p.sizes || []).length} rozm.</p>
                {(p.completeness_gaps || []).length > 0
                  ? <p className="text-[10px] text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{p.completeness_gaps.length} braków</p>
                  : <p className="text-[10px] text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />kompletny</p>}
              </div>
            </button>
            <div className="border-t border-border px-3 py-1.5 text-right">
              <Button data-testid={`b2b-product-del-${p.id}`} size="sm" variant="ghost" className="h-7 text-rose-400 gap-1" onClick={() => del(p)}><Trash2 className="h-3.5 w-3.5" />Usuń</Button>
            </div>
          </div>
        ))}
      </div>
      {open && <ProductForm storeId={storeId} storeName={storeName} product={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
};

const ProductForm = ({ storeId, storeName, product, onClose, onSaved }) => {
  const isEdit = !!product?.id;
  const [f, setF] = useState({ ...EMPTY, ...product });
  const [colorsStr, setColorsStr] = useState((product?.colors || []).join(", "));
  const [sizesStr, setSizesStr] = useState((product?.sizes || []).join(", "));
  const [priceZl, setPriceZl] = useState(product?.price_grosz ? (product.price_grosz / 100).toString() : "");
  const [bases, setBases] = useState([]);
  const [baseDetail, setBaseDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => { api.get("/b2b/admin/base-products").then((r) => setBases(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!f.base_sku) { setBaseDetail(null); return; }
    api.get(`/b2b/admin/base-products/${encodeURIComponent(f.base_sku)}`).then((r) => setBaseDetail(r.data)).catch(() => setBaseDetail(null));
  }, [f.base_sku]);

  const colors = useMemo(() => toArr(colorsStr), [colorsStr]);
  const sizes = useMemo(() => toArr(sizesStr), [sizesStr]);
  const disabled = new Set(f.disabled_variants || []);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const buildPayload = () => ({
    store_id: storeId,
    name: f.name, subtitle: f.subtitle, description: f.description,
    price_grosz: Math.round((parseFloat(priceZl) || 0) * 100),
    photos: f.photos || [], colors, sizes, disabled_variants: [...disabled],
    size_table: f.size_table, sort_order: Number(f.sort_order) || 0, no_branding: !!f.no_branding,
    base_sku: f.base_sku, base_color: f.base_color, base_notes: f.base_notes,
    variant_index: f.variant_index || {}, markings: f.markings || [], production_notes: f.production_notes,
  });

  const save = async (publishAfter) => {
    if (!f.name.trim()) return toast.error("Podaj nazwę produktu");
    setBusy(true);
    try {
      const payload = buildPayload();
      const { data } = isEdit
        ? await api.put(`/b2b/admin/products/${product.id}`, payload)
        : await api.post("/b2b/admin/products", payload);
      if (publishAfter) {
        try { await api.post(`/b2b/admin/products/${data.id}/publish`); toast.success("Produkt opublikowany"); }
        catch (e) {
          const d = e?.response?.data?.detail;
          if (d?.gaps) { toast.error(`Nie opublikowano — braki: ${d.gaps.length}`); setF((prev) => ({ ...prev, _gaps: d.gaps, id: data.id })); setBusy(false); return; }
          throw e;
        }
      } else { toast.success(isEdit ? "Zapisano zmiany" : "Produkt zapisany (roboczy)"); }
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setUploadingPhoto(true);
    try {
      const ids = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data.id);
      }
      set({ photos: [...(f.photos || []), ...ids] });
    } catch (err) { toast.error(errMsg(err)); } finally { setUploadingPhoto(false); }
  };

  const setVIndex = (c, s, val) => set({ variant_index: { ...(f.variant_index || {}), [vKey(c, s)]: val } });
  const toggleVariant = (c, s) => {
    const k = vKey(c, s); const n = new Set(disabled); n.has(k) ? n.delete(k) : n.add(k);
    set({ disabled_variants: [...n] });
  };

  // ---- markings ----
  const addMarking = () => set({ markings: [...(f.markings || []), { place: "Przód", position_desc: "", technique: "DTF", width_mm: "", height_mm: "", dimension_type: "target", graphic_desc: "", color: "", production_file_id: "", viz_file_id: "", notes: "", size_overrides: [] }] });
  const updMarking = (i, patch) => set({ markings: (f.markings || []).map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const delMarking = (i) => set({ markings: (f.markings || []).filter((_, j) => j !== i) });
  const uploadMarkingFile = async (i, field, e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updMarking(i, { [field]: data.id });
      toast.success("Plik dodany");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const addOverride = (i) => updMarking(i, { size_overrides: [...(f.markings[i].size_overrides || []), { size: "", width_mm: "", height_mm: "", position_desc: "" }] });
  const updOverride = (i, j, patch) => updMarking(i, { size_overrides: f.markings[i].size_overrides.map((o, k) => (k === j ? { ...o, ...patch } : o)) });
  const delOverride = (i, j) => updMarking(i, { size_overrides: f.markings[i].size_overrides.filter((_, k) => k !== j) });

  const gaps = f._gaps || product?.completeness_gaps || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="b2b-product-form" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">{isEdit ? "Edycja produktu" : "Nowy produkt"} — {storeName}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Dane „Widok klienta" są widoczne dla klienta. „Karta produkcyjna" jest wewnętrzna — klient jej nie widzi.</DialogDescription>
        </DialogHeader>

        {gaps.length > 0 && (
          <div data-testid="b2b-gaps" className="rounded-lg border border-amber-500/40 bg-amber-400/10 p-3 text-xs text-amber-200">
            <p className="font-bold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Braki do publikacji ({gaps.length}):</p>
            <ul className="mt-1 list-disc pl-5 space-y-0.5">{gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
          </div>
        )}

        {/* ===== WIDOK KLIENTA ===== */}
        <section className="space-y-3 rounded-lg border border-border p-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400">Widok klienta</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Nazwa produktu *</Label><Input data-testid="b2b-pf-name" value={f.name} onChange={(e) => set({ name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Podtytuł</Label><Input data-testid="b2b-pf-subtitle" value={f.subtitle} onChange={(e) => set({ subtitle: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Opis</Label><Textarea data-testid="b2b-pf-desc" value={f.description} onChange={(e) => set({ description: e.target.value })} rows={2} className="mt-1" /></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Cena (zł)</Label><Input data-testid="b2b-pf-price" type="number" min="0" step="0.01" value={priceZl} onChange={(e) => setPriceZl(e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Kolory (po przecinku)</Label><Input data-testid="b2b-pf-colors" value={colorsStr} onChange={(e) => setColorsStr(e.target.value)} placeholder="np. Czarny, Czerwony" className="mt-1" /></div>
            <div><Label className="text-xs">Rozmiary (po przecinku)</Label><Input data-testid="b2b-pf-sizes" value={sizesStr} onChange={(e) => setSizesStr(e.target.value)} placeholder="np. S, M, L, XL" className="mt-1" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Kolejność</Label><Input data-testid="b2b-pf-sort" type="number" value={f.sort_order} onChange={(e) => set({ sort_order: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Tabela rozmiarów (tekst)</Label><Textarea data-testid="b2b-pf-sizetable" value={f.size_table} onChange={(e) => set({ size_table: e.target.value })} rows={1} className="mt-1" /></div>
          </div>
          <div>
            <Label className="text-xs">Zdjęcia / wizualizacje</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(f.photos || []).map((ph, i) => (
                <div key={ph} className="relative h-20 w-20 rounded-lg overflow-hidden border border-border">
                  <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => set({ photos: f.photos.filter((x) => x !== ph) })} className="absolute right-0.5 top-0.5 rounded bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label data-testid="b2b-pf-photo-upload" className="grid h-20 w-20 cursor-pointer place-items-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-amber-400">
                {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} />
              </label>
            </div>
          </div>
          {/* warianty on/off */}
          {colors.length > 0 && sizes.length > 0 && (
            <div>
              <Label className="text-xs">Dostępne warianty (odznacz, aby wyłączyć)</Label>
              <div className="mt-1 overflow-x-auto">
                <table className="text-[11px]"><tbody>
                  {colors.map((c) => (
                    <tr key={c}>
                      <td className="pr-2 font-semibold whitespace-nowrap">{c}</td>
                      {sizes.map((s) => (
                        <td key={s} className="px-1 py-0.5 text-center">
                          <label className="flex flex-col items-center gap-0.5">
                            <span className="text-muted-foreground">{s}</span>
                            <Checkbox data-testid={`b2b-pf-variant-${c}-${s}`} checked={!disabled.has(vKey(c, s))} onCheckedChange={() => toggleVariant(c, s)} />
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}
        </section>

        {/* ===== KARTA PRODUKCYJNA ===== */}
        <section className="space-y-3 rounded-lg border border-indigo-500/40 bg-indigo-500/5 p-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-indigo-300">Karta produkcyjna (wewnętrzna)</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Kartoteka odzieży bazowej (EMPRO)</Label>
              <Select value={f.base_sku || ""} onValueChange={(v) => set({ base_sku: v })}>
                <SelectTrigger data-testid="b2b-pf-base-sku" className="mt-1"><SelectValue placeholder="Wybierz kartotekę bazową" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {bases.map((b) => <SelectItem key={b.sku} value={b.sku} data-testid={`b2b-pf-base-opt-${b.sku}`}>{b.sku} — {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {baseDetail && <p className="mt-1 text-[10px] text-muted-foreground">Baza: {(baseDetail.colors || []).length} kol. · {(baseDetail.sizes || []).length} rozm.{Object.keys(baseDetail.wapro_index || {}).length ? " · ma indeksy WAPRO" : ""}</p>}
            </div>
            <div><Label className="text-xs">Kolor odzieży bazowej</Label><Input data-testid="b2b-pf-base-color" value={f.base_color} onChange={(e) => set({ base_color: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs">Uwagi do odzieży bazowej</Label><Textarea data-testid="b2b-pf-base-notes" value={f.base_notes} onChange={(e) => set({ base_notes: e.target.value })} rows={1} className="mt-1" /></div>

          {/* indeksy wariantów */}
          {colors.length > 0 && sizes.length > 0 && (
            <div>
              <Label className="text-xs">Indeksy wariantów (kolor × rozmiar)</Label>
              <p className="text-[10px] text-muted-foreground">Wpisz indeks wewn./WAPRO dla aktywnych wariantów. Wyłączone warianty pomijane.</p>
              <div className="mt-1 overflow-x-auto">
                <table className="text-[11px] border-collapse">
                  <thead><tr><th className="pr-2"></th>{sizes.map((s) => <th key={s} className="px-1 font-semibold">{s}</th>)}</tr></thead>
                  <tbody>
                    {colors.map((c) => (
                      <tr key={c}>
                        <td className="pr-2 font-semibold whitespace-nowrap">{c}</td>
                        {sizes.map((s) => (
                          <td key={s} className="px-0.5 py-0.5">
                            {disabled.has(vKey(c, s))
                              ? <div className="h-8 w-20 grid place-items-center text-muted-foreground/40">—</div>
                              : <Input data-testid={`b2b-pf-idx-${c}-${s}`} value={(f.variant_index || {})[vKey(c, s)] || ""} onChange={(e) => setVIndex(c, s, e.target.value)} className="h-8 w-20 text-[11px] font-mono" />}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* znakowania */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none">
              <Switch data-testid="b2b-pf-no-branding" checked={!!f.no_branding} onCheckedChange={(v) => set({ no_branding: v })} />
              Produkt bez znakowania
            </label>
            {!f.no_branding && <Button data-testid="b2b-pf-add-marking" size="sm" variant="outline" className="h-8 gap-1.5" onClick={addMarking}><Plus className="h-3.5 w-3.5" />Dodaj znakowanie</Button>}
          </div>

          {!f.no_branding && (f.markings || []).map((m, i) => (
            <div key={i} data-testid={`b2b-pf-marking-${i}`} className="rounded-lg border border-border bg-background/60 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-amber-300">Znakowanie {i + 1}</p>
                <Button size="icon" variant="ghost" data-testid={`b2b-pf-marking-del-${i}`} className="h-7 w-7 text-rose-400" onClick={() => delMarking(i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <div><Label className="text-[11px]">Miejsce</Label>
                  <Select value={m.place || ""} onValueChange={(v) => updMarking(i, { place: v })}><SelectTrigger data-testid={`b2b-pf-marking-place-${i}`} className="mt-1 h-9"><SelectValue placeholder="Miejsce" /></SelectTrigger>
                    <SelectContent>{PLACES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-[11px]">Technika</Label>
                  <Select value={m.technique || ""} onValueChange={(v) => updMarking(i, { technique: v })}><SelectTrigger data-testid={`b2b-pf-marking-tech-${i}`} className="mt-1 h-9"><SelectValue placeholder="Technika" /></SelectTrigger>
                    <SelectContent>{TECHNIQUES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div><Label className="text-[11px]">Dokładny opis położenia</Label><Textarea data-testid={`b2b-pf-marking-pos-${i}`} value={m.position_desc} onChange={(e) => updMarking(i, { position_desc: e.target.value })} rows={1} className="mt-1" placeholder="np. lewa pierś, 8 cm od szwu ramienia" /></div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                <div><Label className="text-[11px]">Szerokość (mm)</Label><Input data-testid={`b2b-pf-marking-w-${i}`} type="number" min="0" value={m.width_mm ?? ""} onChange={(e) => updMarking(i, { width_mm: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 h-9" /></div>
                <div><Label className="text-[11px]">Wysokość (mm)</Label><Input data-testid={`b2b-pf-marking-h-${i}`} type="number" min="0" value={m.height_mm ?? ""} onChange={(e) => updMarking(i, { height_mm: e.target.value === "" ? "" : Number(e.target.value) })} className="mt-1 h-9" /></div>
                <div className="col-span-2"><Label className="text-[11px]">Wymiar</Label>
                  <Select value={m.dimension_type || "target"} onValueChange={(v) => updMarking(i, { dimension_type: v })}><SelectTrigger data-testid={`b2b-pf-marking-dim-${i}`} className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="target">Docelowy</SelectItem><SelectItem value="max">Maksymalny</SelectItem></SelectContent></Select></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <div><Label className="text-[11px]">Opis grafiki</Label><Input data-testid={`b2b-pf-marking-graphic-${i}`} value={m.graphic_desc} onChange={(e) => updMarking(i, { graphic_desc: e.target.value })} className="mt-1 h-9" /></div>
                <div><Label className="text-[11px]">Kolor znakowania</Label><Input data-testid={`b2b-pf-marking-color-${i}`} value={m.color} onChange={(e) => updMarking(i, { color: e.target.value })} className="mt-1 h-9" /></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="flex items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[11px] cursor-pointer hover:border-amber-400">
                  <Upload className="h-3.5 w-3.5" />{m.production_file_id ? "Plik ✓ (zmień)" : "Plik produkcyjny"}
                  <input data-testid={`b2b-pf-marking-file-${i}`} type="file" className="hidden" onChange={(e) => uploadMarkingFile(i, "production_file_id", e)} />
                </label>
                <label className="flex items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[11px] cursor-pointer hover:border-amber-400">
                  <ImageIcon className="h-3.5 w-3.5" />{m.viz_file_id ? "Wizualizacja ✓ (zmień)" : "Wizualizacja rozmieszczenia"}
                  <input data-testid={`b2b-pf-marking-viz-${i}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadMarkingFile(i, "viz_file_id", e)} />
                </label>
                {m.viz_file_id && <ProtectedImg fileId={m.viz_file_id} className="h-9 w-9 rounded object-cover border border-border" />}
              </div>
              <div><Label className="text-[11px]">Uwagi dla produkcji</Label><Textarea data-testid={`b2b-pf-marking-notes-${i}`} value={m.notes} onChange={(e) => updMarking(i, { notes: e.target.value })} rows={1} className="mt-1" /></div>
              {/* wyjątki per rozmiar */}
              <div className="rounded-md border border-border/60 p-2">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold text-muted-foreground">Wyjątki dla rozmiaru ({(m.size_overrides || []).length})</p>
                  <Button size="sm" variant="ghost" data-testid={`b2b-pf-marking-add-override-${i}`} className="h-6 gap-1 text-[11px]" onClick={() => addOverride(i)}><Plus className="h-3 w-3" />Wyjątek</Button>
                </div>
                {(m.size_overrides || []).map((o, j) => (
                  <div key={j} className="mt-1 grid grid-cols-2 sm:grid-cols-5 gap-1.5 items-center">
                    <Input placeholder="Rozmiar" data-testid={`b2b-pf-ovr-size-${i}-${j}`} value={o.size} onChange={(e) => updOverride(i, j, { size: e.target.value })} className="h-8 text-[11px]" />
                    <Input placeholder="szer. mm" type="number" value={o.width_mm ?? ""} onChange={(e) => updOverride(i, j, { width_mm: e.target.value === "" ? "" : Number(e.target.value) })} className="h-8 text-[11px]" />
                    <Input placeholder="wys. mm" type="number" value={o.height_mm ?? ""} onChange={(e) => updOverride(i, j, { height_mm: e.target.value === "" ? "" : Number(e.target.value) })} className="h-8 text-[11px]" />
                    <Input placeholder="położenie" value={o.position_desc} onChange={(e) => updOverride(i, j, { position_desc: e.target.value })} className="h-8 text-[11px] sm:col-span-1" />
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => delOverride(i, j)}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div><Label className="text-xs">Uwagi wewnętrzne (produkcja)</Label><Textarea data-testid="b2b-pf-prod-notes" value={f.production_notes} onChange={(e) => set({ production_notes: e.target.value })} rows={2} className="mt-1" /></div>
        </section>

        <div className="flex flex-wrap gap-2 justify-end sticky bottom-0 bg-background pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Anuluj</Button>
          <Button data-testid="b2b-pf-save-draft" variant="outline" onClick={() => save(false)} disabled={busy}>{busy ? "Zapisywanie…" : "Zapisz roboczy"}</Button>
          <Button data-testid="b2b-pf-save-publish" onClick={() => save(true)} disabled={busy} className="bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">Zapisz i opublikuj</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
